import type { LandingIconName } from './icons';

export type ConnectivePartyKey = 'labs' | 'phy' | 'pat';

export type ConnectiveParty = Readonly<{
  key: ConnectivePartyKey;
  cx: number;
  color: string;
  role: string;
  name: string;
  icon: LandingIconName;
  statValue: string;
  statLabel: string;
  readoutHtml: string;
}>;

export type ConnectiveHandoff = Readonly<{
  from: ConnectivePartyKey;
  to: ConnectivePartyKey;
  label: string;
}>;

export const CONNECTIVE_PARTIES = [
  {
    key: 'labs',
    cx: 200,
    color: 'var(--t-labs)',
    role: 'Source & produce',
    name: 'Labs',
    icon: 'labs',
    statValue: '42 lots',
    statLabel: 'from 3 donor lines',
    readoutHtml:
      '<b>Labs</b> source and release documented stem-cell lines. RegenLoop logs every line and lot at the point of origin, then links that context to outcome patterns that require review.',
  },
  {
    key: 'phy',
    cx: 560,
    color: 'var(--t-phy)',
    role: 'Acquire & administer',
    name: 'Physicians',
    icon: 'phy',
    statValue: '36 sites',
    statLabel: 'point-of-care capture',
    readoutHtml:
      '<b>Physicians</b> acquire and administer the lines. Every dose is captured as structured, comparable data — not free-text notes — and they get protocol-level signals back.',
  },
  {
    key: 'pat',
    cx: 920,
    color: 'var(--t-pat)',
    role: 'Contribute outcomes',
    name: 'Patients',
    icon: 'pat',
    statValue: '1,284',
    statLabel: 'patients tracked',
    readoutHtml:
      '<b>Patients</b> contribute longitudinal outcomes. Standardized follow-up turns patient-reported and clinical data into review-ready context.',
  },
] as const satisfies readonly ConnectiveParty[];

export const CONNECTIVE_HANDOFFS = [
  { from: 'labs', to: 'phy', label: 'stem-cell lines' },
  { from: 'phy', to: 'pat', label: 'treatment' },
] as const satisfies readonly ConnectiveHandoff[];

export const CONNECTIVE_LAYER = {
  key: 'rl',
  name: 'RegenLoop',
  suffix: '.ai',
  role: 'The connective intelligence layer',
  icon: 'rl',
  pills: ['Captures every party', 'Links outcome → line', '7 review signals'],
  readoutHtml:
    '<b>RegenLoop is the connective layer.</b> It captures structured data from every party, links each outcome to line, lot, and protocol context, and surfaces candidate signals that require review.',
  mobileReadout:
    'Captures structured data from every party, links outcomes to line and lot context, and surfaces candidate signals that require review.',
} as const;

export const CONNECTIVE_IDLE_READOUT_HTML =
  '<span class="hint">Three parties, one connective layer. <b style="color:var(--ink-on-dark)">Hover any party</b> to see what it gives the loop — and what it gets back.</span>' as const;

export const CONNECTIVE_CANVAS = {
  width: 1120,
  height: 440,
  cardTop: 58,
  handoffY: 106,
  connectorTop: 190,
  layerTop: 300,
  layerMid: 342,
  layerLeft: 74,
  halfCardWidth: 116,
} as const;
